import { pdfjsLib } from './pdfWorker';

// Chromium's embedded PDF plugin does not reliably print from a display:none
// iframe (especially in Electron). Render the canonical saved PDF into an
// isolated print document, with the original physical size for each page.
export async function printDocument(bytes) {
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes).slice() }).promise;
  const urls = [];
  let frame;
  let cleanupTimer;
  const cleanup = () => {
    clearTimeout(cleanupTimer);
    frame?.remove();
    urls.forEach((url) => URL.revokeObjectURL(url));
  };
  try {
    const pages = [];
    for (let number = 1; number <= pdf.numPages; number++) {
      const page = await pdf.getPage(number);
      const size = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: 2.5 });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport, intent: 'print' }).promise;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error(`Could not prepare page ${number} for printing.`);
      const url = URL.createObjectURL(blob); urls.push(url);
      pages.push({ url, width: size.width, height: size.height });
      canvas.width = 0; canvas.height = 0;
    }
    frame = document.createElement('iframe');
    frame.title = 'Print document';
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0';
    const loaded = new Promise((resolve) => { frame.onload = resolve; });
    frame.srcdoc = `<!doctype html><html><head><title>Print document</title><style>
      html,body{margin:0;padding:0}img{display:block;break-after:page;max-width:none}img:last-child{break-after:auto}
      ${pages.map((page, i) => `@page p${i}{size:${page.width}pt ${page.height}pt;margin:0}`).join('\n')}
      </style></head><body>${pages.map((page, i) => `<img alt="Page ${i + 1}" src="${page.url}" style="page:p${i};width:${page.width}pt;height:${page.height}pt">`).join('')}</body></html>`;
    document.body.appendChild(frame);
    await loaded;
    await Promise.all(Array.from(frame.contentDocument.images).map((image) => image.decode()));
    frame.contentWindow.addEventListener('afterprint', cleanup, { once: true });
    // Keep images alive while the dialog is open; ten-second teardown caused
    // blank output when someone took longer to choose their printer.
    cleanupTimer = setTimeout(cleanup, 30 * 60 * 1000);
    frame.contentWindow.focus();
    frame.contentWindow.print();
  } catch (err) { cleanup(); throw err; }
  finally { await pdf.destroy(); }
}
